<!-- Card de nota: mesmo visual nas notas sugeridas e nos resultados do filtro. -->
<ng-template #cardNota let-item>
  <div class="snf-nota">
    <span class="snf-nota-icone">
      <mat-icon class="material-symbols-outlined">receipt_long</mat-icon>
    </span>
    <!-- A faixa (fornecedor/produto) já é o título da seção: o card não repete. -->
    <div class="snf-nota-textos">
      <span class="snf-nota-titulo">{{ item.rotulo }}</span>
      <span class="snf-nota-meta">
        {{ emitente(item.nota) }} ·
        {{ item.nota.dataEmissao ? 'Emissão ' + (item.nota.dataEmissao | date: 'dd/MM/yyyy') : 'Sem data de emissão' }}
      </span>
    </div>
    <div class="snf-nota-valores">
      <span class="snf-nota-valor">{{ item.nota.valorTotal !== null ? (item.nota.valorTotal | abnt: 'preco' : 2) : 'Sem valor' }}</span>
      @if (item.diferenca !== null) {
        @if (item.diferenca === 0) {
          <span class="status-badge badge-verde">
            <mat-icon class="material-symbols-outlined">check_circle</mat-icon>
            Igual ao pedido
          </span>
        } @else {
          <span class="status-badge badge-amarelo">
            <mat-icon class="material-symbols-outlined">{{ item.diferenca > 0 ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
            {{ valorAbsoluto(item.diferenca) | abnt: 'preco' : 2 }} {{ item.diferenca > 0 ? 'acima' : 'abaixo' }}
          </span>
        }
      }
    </div>
    <span class="snf-nota-acao" [matTooltip]="motivoAcoesDesabilitadas">
      <button mat-flat-button type="button" class="btn-primary snf-vincular btn-spinner"
        [class.btn-loading]="ocupado && idNotaEscolhida === item.nota.idNFeEntrada"
        [disabled]="acoesDesabilitadas"
        [attr.aria-label]="ocupado && idNotaEscolhida === item.nota.idNFeEntrada ? 'Vinculando' : 'Vincular'"
        (click)="escolher(item.nota)">
        <mat-icon class="material-symbols-outlined">link</mat-icon>
        Vincular
        @if (ocupado && idNotaEscolhida === item.nota.idNFeEntrada) { <mat-spinner diameter="20"></mat-spinner> }
      </button>
    </span>
  </div>
</ng-template>

<!-- Estado vazio tracejado do design: ícone, título e apoio. -->
<ng-template #vazio let-icone="icone" let-titulo="titulo" let-texto="texto">
  <div class="snf-vazio">
    <mat-icon class="material-symbols-outlined snf-vazio-icone">{{ icone }}</mat-icon>
    <span class="snf-vazio-titulo">{{ titulo }}</span>
    <span class="snf-vazio-texto">{{ texto }}</span>
  </div>
</ng-template>

<!-- Skeleton da lista enquanto as notas não chegam. -->
<ng-template #carregandoNotas>
  <div class="snf-lista" aria-hidden="true">
    @for (slot of skeletonSlots; track slot) {
      <app-loading-box height="64" width="100%"></app-loading-box>
    }
  </div>
</ng-template>

<div class="snf">
  <!-- Filtro único: vazio mostra as notas sugeridas; com termo, busca na gaveta inteira. -->
  <div class="snf-filtro">
    <mat-form-field appearance="outline" class="snf-filtro-campo" subscriptSizing="dynamic">
      <mat-icon matIconPrefix class="material-symbols-outlined">search</mat-icon>
      <input matInput type="text" autocomplete="off" placeholder="Filtrar por número ou chave de acesso…"
        [disabled]="acoesDesabilitadas" [ngModel]="termo" (ngModelChange)="alterarTermo($event)">
      @if (buscando) {
        <mat-progress-spinner matSuffix class="snf-filtro-spinner" mode="indeterminate" diameter="18"></mat-progress-spinner>
      }
    </mat-form-field>
    <!-- Nota que não chegou pela SEFAZ: importa o XML (importação da Central) e já vincula. -->
    <!-- Nota importada: o vínculo roda com o spinner neste botão (nenhum "Vincular" foi clicado). -->
    <span [matTooltip]="motivoAcoesDesabilitadas">
      <button mat-stroked-button type="button" class="btn-secondary btn-spinner"
        [class.btn-loading]="ocupado && isXmlImportado"
        [disabled]="acoesDesabilitadas" aria-label="Importar XML" (click)="enviarXml()">
        <mat-icon class="material-symbols-outlined">upload_file</mat-icon>
        Importar XML
        @if (ocupado && isXmlImportado) { <mat-spinner diameter="20"></mat-spinner> }
      </button>
    </span>
  </div>

  <!-- Referência das diferenças dos cards: valor do pedido e quantas notas a lista tem. -->
  <div class="snf-resumo">
    @if (valorPedido !== null) {
      <span>Valor do pedido <strong>{{ valorPedido | abnt: 'preco' : 2 }}</strong></span>
      <span class="snf-links-sep">·</span>
    }
    <span>{{ quantidadeDisponivel }} {{ quantidadeDisponivel === 1 ? 'nota disponível' : 'notas disponíveis' }}</span>
  </div>

  @if (avisoChave) {
    <div class="snf-aviso">
      <mat-icon class="material-symbols-outlined">warning</mat-icon>
      <span>{{ avisoChave }}</span>
    </div>
  }

  <div class="snf-conteudo">
    @if (isFiltrando) {
      <!-- ---------------------------------------------------------------- Resultado do filtro -->
      @if (buscando && resultados.length === 0) {
        <ng-container [ngTemplateOutlet]="carregandoNotas"></ng-container>
      } @else if (resultados.length === 0) {
        <ng-container [ngTemplateOutlet]="vazio" [ngTemplateOutletContext]="{
          icone: 'search_off',
          titulo: 'Nenhuma nota encontrada para “' + termo.trim() + '”',
          texto: 'Se a nota ainda não chegou pela SEFAZ, importe o XML.'
        }"></ng-container>
      } @else {
        @for (secao of secoes(resultados); track secao.rotulo) {
          <span class="snf-secao">{{ secao.rotulo }}</span>
          <div class="snf-lista">
            @for (item of secao.itens; track item.nota.idNFeEntrada) {
              <ng-container [ngTemplateOutlet]="cardNota" [ngTemplateOutletContext]="{ $implicit: item }"></ng-container>
            }
          </div>
        }
        @if (temMais) {
          <span class="snf-mais" [matTooltip]="motivoAcoesDesabilitadas">
            <button mat-button type="button" class="btn-ver-mais btn-spinner" [class.btn-loading]="buscando"
              [disabled]="acoesDesabilitadas || buscando"
              [attr.aria-label]="buscando ? 'Carregando mais notas' : 'Carregar mais'" (click)="buscar(true)">
              <span class="material-symbols-outlined">expand_more</span>
              Carregar mais
              @if (buscando) { <mat-spinner diameter="20"></mat-spinner> }
            </button>
          </span>
        }
      }
    } @else {
      <!-- ---------------------------------------------------------------- Notas sugeridas -->
      @if (carregandoSugeridas) {
        <ng-container [ngTemplateOutlet]="carregandoNotas"></ng-container>
      } @else if (sugeridas.length === 0 && semFornecedor) {
        <ng-container [ngTemplateOutlet]="vazio" [ngTemplateOutletContext]="{
          icone: 'person_off',
          titulo: 'O pedido não tem fornecedor definido e nenhuma nota tem produto dele',
          texto: 'Filtre por número ou chave para buscar na gaveta ou importe o XML.'
        }"></ng-container>
      } @else if (sugeridas.length === 0) {
        <ng-container [ngTemplateOutlet]="vazio" [ngTemplateOutletContext]="{
          icone: 'inbox',
          titulo: 'Nenhuma nota capturada para este fornecedor ou com produto do pedido',
          texto: 'Se a nota ainda não chegou pela SEFAZ, importe o XML.'
        }"></ng-container>
      } @else {
        @for (secao of secoes(sugeridas); track secao.rotulo) {
          <span class="snf-secao">{{ secao.rotulo }}</span>
          <div class="snf-lista">
            @for (item of secao.itens; track item.nota.idNFeEntrada) {
              <ng-container [ngTemplateOutlet]="cardNota" [ngTemplateOutletContext]="{ $implicit: item }"></ng-container>
            }
          </div>
        }
        @if (totalSugeridas > sugeridas.length) {
          <span class="snf-rodape-lista">
            Mostrando {{ sugeridas.length }} de {{ totalSugeridas }} notas sugeridas — filtre por número ou chave para encontrar as demais.
          </span>
        }
      }
    }
  </div>

</div>
